import React from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #2d3436;
  border-radius: 8px;
  padding: 2rem;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid #3d4346;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #dfe6e9;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    color: #74b9ff;
  }
`;

const GameImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  color: #dfe6e9;
  margin: 0 0 1rem 0;
`;

const ShortDescription = styled.p`
  color: #b2bec3;
  font-style: italic;
  margin-bottom: 1.5rem;
`;

const LongDescription = styled.div`
  color: #dfe6e9;
  margin-bottom: 1rem;
  line-height: 1.5;

  p {
    margin-bottom: 1rem;
  }

  h3 {
    color: #74b9ff;
    margin: 1rem 0 0.5rem 0;
    font-size: 1.1rem;
    border-bottom: 1px solid #3d4346;
    padding-bottom: 0.25rem;
  }

  ul {
    margin: 0.5rem 0;
    padding-left: 0;
    list-style: none;
  }

  li {
    margin-bottom: 0.5rem;
    position: relative;
    padding-left: 1.25rem;
    text-align: left;
    font-size: 0.95rem;

    &:before {
      content: "•";
      color: #74b9ff;
      position: absolute;
      left: 0;
      font-size: 1.1rem;
      line-height: 1;
    }
  }

  strong {
    color: #74b9ff;
    font-weight: 500;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const Link = styled.a`
  background-color: #74b9ff;
  color: #2d3436;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: #0984e3;
  }
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  game: {
    title: string;
    subtitle: string;
    imageUrl: string;
    itchUrl: string;
    contributors: string[];
    longDescription: string;
    sourceUrl?: string;
  };
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, game }) => {
  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        <GameImage src={game.imageUrl} alt={game.title} />
        <Title>{game.title}</Title>
        <ShortDescription>{game.subtitle}</ShortDescription>
        <LongDescription dangerouslySetInnerHTML={{ __html: game.longDescription }} />
        <LinksContainer>
          <Link href={game.itchUrl} target="_blank" rel="noopener noreferrer">
            View on itch.io
          </Link>
          {game.sourceUrl && (
            <Link href={game.sourceUrl} target="_blank" rel="noopener noreferrer">
              View Source Code
            </Link>
          )}
        </LinksContainer>
      </ModalContent>
    </Overlay>
  );
};

export default Modal; 